import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { format, parseISO } from 'date-fns';
import { XIcon, PencilIcon, TrashIcon, PlusIcon, ClockIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Main component
const SessionManagement = () => {
  const { t } = useTranslation('sessionmanagement');
  const { api, user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    weekdays: [],
    description: ''
  });

  // Fetch sessions on component mount
  useEffect(() => {
    fetchSessions();
  }, []);

  // Fetch sessions from API
  const fetchSessions = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/sessions`);
      setSessions(response.data.items);
      setError(null);
    } catch (err) {
      setError(t('error'));
      console.error('Error fetching sessions:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle weekday selection
  const handleWeekdayChange = (day) => {
    setFormData(prev => {
      const weekdays = [...prev.weekdays];
      if (weekdays.includes(day)) {
        return { ...prev, weekdays: weekdays.filter(d => d !== day) };
      } else {
        return { ...prev, weekdays: [...weekdays, day] };
      }
    });
  };

  // Open create modal
  const openCreateModal = () => {
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      weekdays: [],
      description: ''
    });
    setIsCreateModalOpen(true);
  };

  // Open edit modal
  const openEditModal = (session) => {
    setSelectedSession(session);
    setFormData({
      name: session.name,
      start_date: session.start_date,
      end_date: session.end_date,
      start_time: session.start_time.replace('Z', ''),
      end_time: session.end_time.replace('Z', ''),
      weekdays: session.weekdays,
      description: session.description,
      status: session.status
    });
    setIsEditModalOpen(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (session) => {
    setSelectedSession(session);
    setIsDeleteModalOpen(true);
  };

  // Create new session
  const createSession = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        start_time: `${formData.start_time}Z`,
        end_time: `${formData.end_time}Z`,
      };

      await api.post(`/api/v1/admin/schools/${user.school_reg}/sessions`, payload);
      setIsCreateModalOpen(false);
      fetchSessions();
    } catch (err) {
      console.error('Error creating session:', err);
      alert('Failed to create session. Please try again.');
    }
  };

  // Update session
  const updateSession = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        start_date: formData.start_date,
        end_date: formData.end_date,
        start_time: `${formData.start_time}Z`,
        end_time: `${formData.end_time}Z`,
        weekdays: formData.weekdays,
        description: formData.description
      };

      await api.patch(
        `/api/v1/admin/schools/${user.school_reg}/sessions/${selectedSession.id}`,
        payload
      );
      setIsEditModalOpen(false);
      fetchSessions();
    } catch (err) {
      console.error('Error updating session:', err);
      alert('Failed to update session. Please try again.');
    }
  };

  // Delete session
  const deleteSession = async () => {
    try {
      await api.delete(`/api/v1/admin/schools/${user.school_reg}/sessions/${selectedSession.id}`);
      setIsDeleteModalOpen(false);
      fetchSessions();
    } catch (err) {
      console.error('Error deleting session:', err);
      alert('Failed to delete session. Please try again.');
    }
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Remove Z suffix if exists
    const cleanTime = timeString.replace('Z', '');
    // Extract hours and minutes
    const [hours, minutes] = cleanTime.split(':');
    return `${hours}:${minutes}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const weekdayOptions = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('title')}</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-3 py-2 text-sm text-white bg-[#0E5252] hover:bg-[#106363] rounded-md transition-colors duration-200"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          {t('createButton')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 text-red-700 p-4 rounded-lg">{error}</div>
      ) : sessions.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium text-gray-600">{t('noSessionsTitle')}</h3>
          <p className="text-gray-500 mt-2">{t('noSessionsDesc')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sessions.map((session) => (
            <div
              key={session.id}
              className={`rounded-lg shadow-md overflow-hidden border-l-4 ${session.status === 'active' ? 'border-green-500' : 'border-yellow-500'
                }`}
            >
              <div className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-gray-800 truncate">{session.name}</h3>
                  <span className={`text-xs font-medium px-2 py-1 rounded-md ${session.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                    {session.status === 'active' ? t('statusActive') : session.status}
                  </span>
                </div>

                <div className="text-sm text-gray-600">
                  <div className="flex items-center mb-2">
                    <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" />
                    <span>{formatDate(session.start_date)} - {formatDate(session.end_date)}</span>
                  </div>

                  <div className="flex items-center mb-2">
                    <ClockIcon className="w-4 h-4 mr-2 text-gray-500" />
                    <span>{formatTime(session.start_time)} - {formatTime(session.end_time)}</span>
                  </div>

                  <div className="mb-2">
                    <div className="flex flex-wrap gap-1 mt-1">
                      {session.weekdays.map((day) => (
                        <span key={`${session.id}-${day}`} className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-md">
                          {t(`days.${day}`)}
                        </span>
                      ))}
                    </div>
                  </div>

                  {session.description && (
                    <p className="text-gray-600 mt-2 line-clamp-2">{session.description}</p>
                  )}
                </div>
              </div>

              <div className="flex border-t border-gray-100">
                <button
                  onClick={() => openEditModal(session)}
                  className="flex-1 py-3 flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <PencilIcon className="w-4 h-4 mr-1" />
                  <span>{t('editButton')}</span>
                </button>
                <div className="w-px bg-gray-100"></div>
                <button
                  onClick={() => openDeleteModal(session)}
                  className="flex-1 py-3 flex items-center justify-center text-red-600 hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="w-4 h-4 mr-1" />
                  <span>{t('deleteButton')}</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Session Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">{t('createModal.title')}</h3>
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={createSession}>
                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.startDate')}</label>
                      <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.endDate')}</label>
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.startTime')}</label>
                      <input
                        type="time"
                        name="start_time"
                        value={formData.start_time}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.endTime')}</label>
                      <input
                        type="time"
                        name="end_time"
                        value={formData.end_time}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('createModal.weekdays')}</label>
                    <div className="flex flex-wrap gap-2">
                      {weekdayOptions.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleWeekdayChange(day)}
                          className={`px-3 py-2 text-xs font-medium rounded-md ${formData.weekdays.includes(day)
                              ? 'bg-blue-100 text-blue-800 border-blue-300'
                              : 'bg-gray-100 text-gray-800 border-gray-200'
                            } border`}
                        >
                          {t(`days.${day}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.description')}</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    ></textarea>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  >
                    {t('createModal.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex items-center px-3 py-2 text-sm text-white bg-[#0E5252] hover:bg-[#106363] rounded-md transition-colors duration-200"
                  >
                    {t('createModal.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {isEditModalOpen && selectedSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="flex justify-between items-center p-6 border-b">
                <h3 className="text-lg font-medium text-gray-900">{t('editModal.title')}</h3>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={updateSession}>
                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.name')}</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.startDate')}</label>
                      <input
                        type="date"
                        name="start_date"
                        value={formData.start_date}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.endDate')}</label>
                      <input
                        type="date"
                        name="end_date"
                        value={formData.end_date}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.startTime')}</label>
                      <input
                        type="time"
                        name="start_time"
                        value={formData.start_time}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.endTime')}</label>
                      <input
                        type="time"
                        name="end_time"
                        value={formData.end_time}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('createModal.weekdays')}</label>
                    <div className="flex flex-wrap gap-2">
                      {weekdayOptions.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleWeekdayChange(day)}
                          className={`px-3 py-2 text-xs font-medium rounded-md ${formData.weekdays.includes(day)
                              ? 'bg-blue-100 text-blue-800 border-blue-300'
                              : 'bg-gray-100 text-gray-800 border-gray-200'
                            } border`}
                        >
                          {t(`days.${day}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('createModal.description')}</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    ></textarea>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                  >
                    {t('createModal.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex items-center px-3 py-2 text-sm text-white bg-[#0E5252] hover:bg-[#106363] rounded-md transition-colors duration-200"
                  >
                    {t('editModal.update')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">{t('deleteModal.title')}</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('deleteModal.confirmMessage', { name: selectedSession.name })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={deleteSession}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('deleteModal.delete')}
                </button>
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {t('deleteModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagement;